import * as Sentry from '@sentry/react';
import { appStore } from '@store/index';
import { getPlatform } from '@utils/analytics/platform';
import { ModuleName } from '@utils/constants';

// Observabilidad unificada del flujo de impresión (web + Electron + multi-sucursal).
//
// Objetivo: que cada falla de impresión llegue a Sentry con CONTEXTO suficiente para
// triar sin depender de capturas de pantalla del usuario. Antes, la falla más común
// (impresora no seleccionada) solo mostraba un toast y no se reportaba; y los eventos
// que sí se reportaban iban sin plataforma, versión del shell, comercio, rol ni estado
// multi-sucursal.

// Tipos de incidente de impresión. Se usan como tag `print_error` y como fingerprint
// para que Sentry agrupe por causa y no por stack.
export type PrintErrorTag =
  | 'printer_not_selected' // electronPrinterName nulo al imprimir (no hay impresora elegida en el equipo)
  | 'getPrinters_no_handler' // el shell Electron no expone el IPC getPrinters (shell desactualizado)
  | 'print_ipc_failure' // falló el IPC 'print' (impresión Electron)
  | 'web_print_failure'; // falló la generación/impresión en navegador (no Electron)

export type PrintTrigger = 'manual' | 'auto' | 'self_service' | 'init' | 'web';

type CapturePrintErrorOptions = {
  tag: PrintErrorTag;
  trigger?: PrintTrigger;
  extra?: Record<string, unknown>;
};

const hasModuleFromStore = (moduleId: (typeof ModuleName)[keyof typeof ModuleName]): boolean => {
  const modules = appStore.getState().subscription?.modulesAndPermissions ?? [];
  return modules.some((m) => m.id === moduleId);
};

const readActiveCommerceId = (): string | null => {
  try {
    return typeof window !== 'undefined' ? localStorage.getItem('id_commerce') : null;
  } catch {
    return null;
  }
};

// Contexto que se adjunta a TODO evento de impresión. Síncrono: todo sale de Redux,
// localStorage y el bridge de plataforma (la versión del shell ya vive en el comercio
// vía useInitElectronVersion → no requiere IPC).
export const buildPrintContext = (): Record<string, unknown> => {
  const state = appStore.getState();
  const commerce = state.commerce;
  const subscription = state.subscription;
  const printSettings = state.electronPrintSettings ?? {};
  const printers = state.electronPrinters ?? [];

  const activeCommerceId = readActiveCommerceId();
  const homeCommerceId = subscription?.homeCommerce?.id_comercio ?? null;
  const printerSelected = Object.keys(printSettings).some((key) => Boolean(printSettings[key]?.electronPrinterName));

  return {
    platform: getPlatform(),
    desktop_app_version: commerce?.electron_app_version ?? null,
    commerce_id: commerce?.id || null,
    commerce_id_path: commerce?.id_path || null,
    account_id: subscription?.accountId ?? null,
    is_root: Boolean(subscription?.isRoot),
    has_auto_print: hasModuleFromStore(ModuleName.IMPRESION_AUTOMATICA),
    has_multisucursal: hasModuleFromStore(ModuleName.PEDIDOS_MULTISUCURSAL),
    in_foreign_branch: Boolean(activeCommerceId && homeCommerceId && activeCommerceId !== homeCommerceId),
    printer_selected: printerSelected,
    printers_enumerated: printers.length,
  };
};

// Reporta una falla de impresión a Sentry con tag, fingerprint y contexto completo.
export const capturePrintError = (error: unknown, options: CapturePrintErrorOptions): void => {
  const context = { ...buildPrintContext(), ...(options.extra ?? {}) };
  Sentry.withScope((scope) => {
    scope.setTag('print_error', options.tag);
    scope.setTag('print_platform', String(context.platform));
    if (options.trigger) scope.setTag('print_trigger', options.trigger);
    scope.setFingerprint(['printing', options.tag]);
    scope.setContext('printing', context);
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
  });
};
